import { useCallback, useState } from 'react';
import type { FC } from 'react';
import { Button } from '@chakra-ui/react';
import { useHistory } from 'react-router';
import { type PaymentMethodConfig, PaymentMethodId } from '../../../bff-api/baskets/model';
import { useBasket } from '../../../bff-api/baskets/useBasket';
import { useDeletePaymentExceptMethodIds } from '../../../bff-api/baskets/useDeletePaymentExceptMethodIds';
import { getKioskLocationId } from '../../../recaptcha/recaptcha-utils';
import { AfterPayIcon } from '../../icons';
import { getJson } from '../../utils';

const URL_AFTERPAY_SUBMIT_ORDER =
    '/on/demandware.store/Sites-SLT-Site/default/PwaCOPlaceOrder-SubmitAfterPayOrder';

interface AfterpayButtonProps {
    afterpayConfig: PaymentMethodConfig;
}

interface ISubmitAfterPayOrderResponse {
    success?: boolean;
    token?: string;
}

export const AfterpayButton: FC<AfterpayButtonProps> = ({ afterpayConfig }) => {
    const [isLoading, setLoading] = useState(false);
    const basket = useBasket().data;
    const deletePaymentExceptMethodIds = useDeletePaymentExceptMethodIds();
    const history = useHistory();

    const { checkoutURL } = afterpayConfig;

    const onClickCheckoutWithAfterPay = useCallback(async () => {
        if (!checkoutURL) {
            return;
        }

        setLoading(true);

        await deletePaymentExceptMethodIds.mutateAsync([PaymentMethodId.AFTER_PAY]);

        const createAfterPayOrderURL = getAfterPaySubmitOrderUrl();
        const resp = await getJson<ISubmitAfterPayOrderResponse>(createAfterPayOrderURL);

        if (resp.success && resp.token) {
            window.location.href = `${checkoutURL}${resp.token}`;
        } else {
            setLoading(false);
            history.push({
                search: '?unexpected-error=true',
            });
        }
    }, [checkoutURL, deletePaymentExceptMethodIds]);

    if (!basket?.orderTotal) {
        return null;
    }

    return (
        <Button
            id="afterpay-express-button"
            data-testid="afterpay-express-button"
            width="100%"
            marginTop={4}
            padding={2}
            isDisabled={!checkoutURL}
            disabled={!checkoutURL}
            isLoading={isLoading}
            onClick={onClickCheckoutWithAfterPay}
            rightIcon={<AfterPayIcon height="16px" width="84px" />}
        />
    );
};

const getAfterPaySubmitOrderUrl: () => string = () => {
    const afterPaySubmitOrderUrl = new URL(URL_AFTERPAY_SUBMIT_ORDER, window.location.origin);
    const kioskLocationId = getKioskLocationId();
    if (kioskLocationId) {
        afterPaySubmitOrderUrl.searchParams.append('kiosk-location-id', kioskLocationId);
    }
    return afterPaySubmitOrderUrl.toString();
};
